import { useMemo } from 'react';

import { Box, Button, Heading, Icon, Modal, Paragraph, Text } from '@zillow/constellation-10';

import { PREMIUM_FEATURES_BY_CONTEXT } from '../../../../srcLegacy/shared/constants/FreemiumConstants';
import ModularListingCSEvents from '../../../../srcLegacy/shared/model/ClickstreamEvent/ModularListingCSEvents';
import { PRODUCT_TYPE_NAME_TO_ID } from '../../../constants/FreemiumConstants';

import MultiplexWarningBanner from '../../MultiplexWarningBanner';

import type { GetUserPropertyDetailsFreemiumProductWebDto } from '@zg-rentals/ts-rental-manager-api-beta-client';

const PremiumPurchaseModal = ({
  onClose,
  onSelectUpgrade,
  freemiumProducts,
  isInBuildingGroup = false,
}: {
  onClose: () => void;
  onSelectUpgrade: () => void;
  freemiumProducts: Array<GetUserPropertyDetailsFreemiumProductWebDto>;
  isInBuildingGroup?: boolean;
}) => {
  const upgradedProduct = useMemo(
    () => freemiumProducts.find((product) => product.type === PRODUCT_TYPE_NAME_TO_ID.PREMIUM),
    [freemiumProducts],
  );

  const renderPremiumUpsellItemList = () => {
    return Object.entries(PREMIUM_FEATURES_BY_CONTEXT).map(([key, value]) => {
      const { HEADING, BODY, ICON: IconComponent } = value.PAY_AND_PUBLISH;

      return (
        <Box key={key} css={{ display: 'flex', flexDirection: 'row', marginBottom: '600' }} asChild>
          <li>
            {/* need div container to prevent mismatching icon sizes */}
            <div>
              <Icon size="lg" css={{ marginRight: '400', color: 'icon.subtle' }} render={<IconComponent />} />
            </div>

            <Box>
              <Paragraph textStyle="body-lg-bold">{HEADING}</Paragraph>
              <Paragraph textStyle="body">{BODY}</Paragraph>
            </Box>
          </li>
        </Box>
      );
    });
  };

  return (
    <Modal
      data-testid="PremiumPurchaseModal"
      size={{
        base: 'fullScreen',
        lg: 'md',
      }}
      open={true}
      onOpenChange={(open) => !open && onClose()}
      header={
        <Heading level={2} textStyle="heading-xs">
          About premium
        </Heading>
      }
      body={
        <div>
          <MultiplexWarningBanner isInBuildingGroup={isInBuildingGroup} />
          <Heading css={{ marginBottom: '800' }} level={3}>
            Get the edge you need to fill your listing fast
          </Heading>
          <div>{renderPremiumUpsellItemList()}</div>
          <Box
            css={{
              paddingTop: '800',
              paddingBottom: '600',
              display: 'flex',
              justifyContent: 'space-between',
              borderTop: '1px solid {colors.border.subtle}',
            }}
          >
            <Text textStyle="heading-xs">Upgrade your listing to premium</Text>
            <Text textStyle="body-lg-bold">{upgradedProduct?.price}</Text>
          </Box>
        </div>
      }
      footer={
        <Button
          size="md"
          tone="brand"
          emphasis="filled"
          onClick={onSelectUpgrade}
          data-eventid={ModularListingCSEvents.ClickUpgradeToPremiumFromModal.data_event_id}
          data-testid="PremiumPurchaseModal-UpgradeToPremiumButton"
        >
          Upgrade to premium
        </Button>
      }
    />
  );
};

export default PremiumPurchaseModal;
